<script setup lang="ts">
import useSecurityChecks from '~/composables/useSecurityChecks'

// Define interfaces for type safety
interface NewsletterCategory {
  id: number
  name: string
  slug: string
  description: string
  icon: string
  active: boolean
}

interface NewsletterData {
  id: number
  slug: string
  categoryId: number
  status: string
  jsonPayload: {
    title: string
    subject: string
    formattedDate: string
    groups: Array<{
      name: string
      icon: string
      articles: Array<{
        title: string
        summary: string
        url: string
        urlToImage: string
        readLength: string
      }>
    }>
  }
  generatedAt: string
  category: NewsletterCategory
}

const route = useRoute()
const category = route.params.category as string
const date = route.params.date as string
const toast = useToast()
const router = useRouter()

// Fetch the newsletter data
const { data: newsletter, error } = await useFetch<NewsletterData>(
  `/api/newsletters/${category}/${date}`,
  {
    key: `newsletter-${category}-${date}`,
    server: true,
    lazy: false,
  },
)

// If newsletter not found, throw 404
if (error.value || !newsletter.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Newsletter not found',
    fatal: true,
  })
}

// Email subscription state
const email = ref('')
const loading = ref(false)
const honeypot = ref('')
const emailInput = ref(null)

// Simple scroll tracking for CTA
const showSubscribeCTA = ref(false)

// Calculate container height based on viewport
const { height: windowHeight } = useWindowSize()
const contentHeight = computed(() => {
  // Content height = viewport height - 170px (trigger point)
  // This ensures user can't scroll further once CTA appears
  return Math.max(windowHeight.value - 170, 200) + 'px'
})

// Add security checks
const { getSecurityData } = useSecurityChecks()

// Parse newsletter data
const newsletterData = computed(() => {
  if (!newsletter.value?.jsonPayload) return null
  return newsletter.value.jsonPayload
})

// Get first group for display
const visibleGroups = computed(() => {
  if (!newsletterData.value?.groups) return []
  return newsletterData.value.groups.slice(0, 1)
})

// Get second group for title/icon display
const secondGroup = computed(() => {
  if (!newsletterData.value?.groups || newsletterData.value.groups.length < 2)
    return null
  return newsletterData.value.groups[1]
})

// Set SEO meta
useSeoMeta({
  title: computed(() => {
    if (newsletterData.value) {
      return `${newsletterData.value.title} • ${newsletterData.value.formattedDate} • AERO.NOW`
    }
    return 'AERO.NOW • Newsletter'
  }),
  ogTitle: computed(() => {
    if (newsletterData.value) {
      return `${newsletterData.value.title} • ${newsletterData.value.formattedDate} • AERO.NOW`
    }
    return 'AERO.NOW • Newsletter'
  }),
  twitterTitle: computed(() => {
    if (newsletterData.value) {
      return `${newsletterData.value.title} • ${newsletterData.value.formattedDate} • AERO.NOW`
    }
    return 'AERO.NOW • Newsletter'
  }),
  ogImage: computed(() => {
    if (newsletterData.value?.groups?.[0]?.articles?.[0]?.urlToImage) {
      return newsletterData.value.groups[0].articles[0].urlToImage
    }
    return undefined
  }),
  twitterImage: computed(() => {
    if (newsletterData.value?.groups?.[0]?.articles?.[0]?.urlToImage) {
      return newsletterData.value.groups[0].articles[0].urlToImage
    }
    return undefined
  }),
  description: computed(() => {
    if (newsletterData.value?.groups?.[0]?.articles?.[0]) {
      return newsletterData.value.groups[0].articles[0].summary
    }
    return 'Get curated aerospace industry insights delivered to your inbox.'
  }),
  twitterDescription: computed(() => {
    if (newsletterData.value?.groups?.[0]?.articles?.[0]) {
      return newsletterData.value.groups[0].articles[0].summary
    }
    return 'Get curated aerospace industry insights delivered to your inbox.'
  }),
})

// Simple scroll detection - client only
onMounted(() => {
  if (!import.meta.client) return

  const handleScroll = () => {
    const scrollY = window.scrollY

    // Show CTA after scrolling 170px down
    if (scrollY > 170 && !showSubscribeCTA.value) {
      showSubscribeCTA.value = true
    }

    // Hide CTA when scrolling back up above trigger point
    if (scrollY <= 170 && showSubscribeCTA.value) {
      showSubscribeCTA.value = false
    }
  }

  window.addEventListener('scroll', handleScroll)

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
  })
})

// Function to modify article URLs for website viewing
function getWebsiteUrl(originalUrl: string): string {
  try {
    const url = new URL(originalUrl)
    const params = new URLSearchParams(url.search)

    // Replace utm_medium=email with utm_medium=website
    if (params.get('utm_medium') === 'email') {
      params.set('utm_medium', 'website')
    }

    url.search = params.toString()
    return url.toString()
  } catch {
    // If URL parsing fails, return original URL
    return originalUrl
  }
}

// Subscribe function
async function subscribe() {
  if (loading.value) return

  if (!email.value) {
    toast.add({
      title: 'Error',
      description: 'Please enter your email',
      color: 'red',
    })
    return
  }

  loading.value = true

  try {
    const securityData = getSecurityData()

    const payload = {
      email: email.value,
      code: null,
      codeType: 'none',
      honeypot: honeypot.value,
      security: securityData,
      flow_resumed: false,
    }

    const response = await $fetch('/api/waitlist/join', {
      method: 'POST',
      body: payload,
    })

    if (
      response &&
      typeof response === 'object' &&
      'success' in response &&
      response.success
    ) {
      const typedResponse = response as Record<string, unknown>

      const baseQuery = {
        email: email.value,
        category: newsletter.value?.category?.slug || 'aerospace',
        isMain: 'false',
        flow: 'newsletter',
      }

      if ('skippedWaitlist' in typedResponse && typedResponse.skippedWaitlist) {
        router.push({
          path: '/subscribe',
          query: {
            ...baseQuery,
            skippedWaitlist: 'true',
            uuid: String(typedResponse.uuid || ''),
            skipCode: String(typedResponse.skipCode || ''),
          },
        })
      } else {
        router.push({
          path: '/subscribe',
          query: {
            ...baseQuery,
            position:
              'position' in typedResponse
                ? String(typedResponse.position)
                : '0',
            referralToken:
              'referralToken' in typedResponse
                ? String(typedResponse.referralToken)
                : '',
          },
        })
      }
    }
  } catch (error: unknown) {
    console.error('Subscription error:', error)
    const errorMessage =
      error &&
      typeof error === 'object' &&
      'data' in error &&
      error.data &&
      typeof error.data === 'object' &&
      'message' in error.data
        ? String(error.data.message)
        : 'Failed to subscribe'

    toast.add({
      title: 'Error',
      description: errorMessage,
      color: 'red',
    })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div>
    <UContainer>
      <UPage>
        <ULandingSection class="py-6">
          <template #title>
            <div v-if="newsletterData">
              <span>{{ newsletter?.category?.icon }}</span>
              <h1
                class="text-4xl flex items-center justify-center gap-3 mt-2 mb-4 uppercase"
              >
                <span>{{ newsletterData.title }}</span>
              </h1>
              <div
                class="text-gray-500 dark:text-gray-400 text-center flex items-center justify-center"
              >
                <div class="inline-flex items-center gap-4">
                  <UBadge
                    :label="newsletterData.formattedDate"
                    size="md"
                    variant="subtle"
                  />
                </div>
              </div>
            </div>
            <div v-else class="text-center">
              <div class="text-2xl mb-4">
                {{ newsletter?.category?.icon || '🚀' }}
              </div>
              <h1 class="text-4xl font-bold mb-4 uppercase">Loading...</h1>
              <div class="text-gray-500 dark:text-gray-400">
                <UBadge label="Loading..." size="md" variant="subtle" />
              </div>
            </div>
          </template>

          <UPageBody ref="contentRef" class="max-w-xl mx-auto">
            <!-- Newsletter Content - Limited height to trigger CTA at 170px scroll -->
            <div class="overflow-hidden" :style="{ maxHeight: contentHeight }">
              <!-- Newsletter Groups -->
              <div v-if="visibleGroups.length" class="space-y-24">
                <div
                  v-for="(group, groupIndex) in visibleGroups"
                  :key="groupIndex"
                  class="space-y-6"
                >
                  <!-- Group Header -->
                  <div
                    class="flex flex-col items-center justify-center text-center gap-2 pb-8"
                  >
                    <span class="text-2xl">{{ group.icon }}</span>
                    <h2 class="text-3xl font-bold uppercase">
                      {{ group.name }}
                    </h2>
                  </div>

                  <!-- Articles -->
                  <div class="space-y-8 pb-12">
                    <article
                      v-for="(article, articleIndex) in group.articles"
                      :key="articleIndex"
                      class="space-y-4"
                    >
                      <!-- Article Content -->
                      <div class="space-y-3">
                        <div
                          class="flex items-center gap-2 text-sm text-gray-500"
                        >
                          <UIcon name="i-heroicons-clock" />
                          <span>{{ article.readLength }} minute read</span>
                        </div>

                        <h3
                          class="text-2xl font-semibold leading-tight text-left"
                        >
                          <a
                            :href="getWebsiteUrl(article.url)"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="hover:text-primary transition-colors duration-200 underline"
                          >
                            {{ article.title }}
                          </a>
                        </h3>

                        <p
                          class="text-gray-700 dark:text-gray-300 leading-relaxed text-left"
                        >
                          {{ article.summary }}
                        </p>
                      </div>
                    </article>
                  </div>
                </div>
              </div>

              <!-- Second Group Title and Icon (if exists) -->
              <div v-if="secondGroup" class="space-y-2 pt-2">
                <div class="flex flex-col items-center justify-center gap-2">
                  <span class="text-2xl">{{ secondGroup.icon }}</span>
                  <h2 class="text-3xl font-bold uppercase">
                    {{ secondGroup.name }}
                  </h2>
                </div>
              </div>
            </div>

            <!-- Loading fallback for when no content -->
            <div
              v-if="!visibleGroups.length && !newsletterData"
              class="space-y-8 animate-pulse"
            >
              <div class="text-center space-y-4">
                <div
                  class="h-8 bg-gray-200 dark:bg-gray-700 rounded mx-auto w-32"
                ></div>
                <div
                  class="h-12 bg-gray-200 dark:bg-gray-700 rounded mx-auto w-64"
                ></div>
              </div>
              <div class="space-y-6">
                <div
                  class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"
                ></div>
                <div class="h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div
                  class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"
                ></div>
              </div>
            </div>
          </UPageBody>
        </ULandingSection>
      </UPage>
    </UContainer>

    <!-- Gradient overlay above subscribe CTA -->
    <div
      v-if="showSubscribeCTA"
      class="fixed left-0 right-0 z-40"
      style="bottom: 50vh; height: 20vh"
    >
      <div
        class="absolute inset-0 bg-gradient-to-t from-white to-white/0 dark:from-gray-900 dark:to-gray-900/0"
      ></div>
    </div>

    <!-- Subscribe to Keep Reading CTA -->
    <div
      v-if="showSubscribeCTA"
      class="fixed bottom-0 left-0 right-0 z-50"
      style="height: 50vh"
    >
      <!-- Fully opaque background container -->
      <div class="absolute inset-0 bg-white dark:bg-gray-900"></div>

      <!-- Content container -->
      <div class="relative h-full flex items-center justify-center pb-safe">
        <UContainer class="w-full">
          <div class="py-8 text-center space-y-4">
            <h3 class="text-2xl font-bold">Subscribe to keep reading</h3>
            <p class="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              This content is completely free, but you must be subscribed to
              {{ newsletter?.category?.name }} to continue reading.
            </p>

            <!-- Email Subscription Form -->
            <ULandingSection class="w-full mx-auto py-0 pt-2 sm:py-0">
              <template #description>
                <span
                  >Busy? Get aerospace insights in just<br />
                  <strong>5 minutes per day 🚀</strong></span
                >
              </template>
              <template #headline>
                <form class="max-w-lg mx-auto" @submit.prevent="subscribe">
                  <UFormGroup :ui="{ container: 'mt-3' }">
                    <UInput
                      ref="emailInput"
                      v-model="email"
                      type="email"
                      placeholder="Enter your email"
                      :ui="{ icon: { trailing: { pointer: '' } } }"
                      required
                      size="xl"
                      autocomplete="off"
                      class="w-full"
                      input-class="rounded-full w-[300px] sm:w-[350px] border-2 border-primary focus:ring-primary focus:border-primary focus:outline-none text-truncate pr-[125px]"
                    >
                      <template #trailing>
                        <UButton
                          type="submit"
                          size="xs"
                          color="primary"
                          :label="loading ? 'Subscribing' : 'Subscribe Free'"
                          :loading="loading"
                        />
                      </template>
                    </UInput>
                  </UFormGroup>
                  <!-- Honeypot field - hidden from real users -->
                  <input
                    v-model="honeypot"
                    type="text"
                    name="contact-preference"
                    aria-hidden="true"
                    class="absolute top-0 left-0 h-0 w-0 -z-50 opacity-0 pointer-events-none"
                    tabindex="-1"
                    autocomplete="off"
                  />
                </form>

                <p class="text-gray-500 mt-2 font-normal text-xs">
                  <UIcon
                    name="i-heroicons-shield-check"
                    class="text-gray-400 translate-y-[1px]"
                  />
                  No spam. Unsubscribe at any time in one click.
                </p>
              </template>
            </ULandingSection>
          </div>
        </UContainer>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Ensure the CTA doesn't interfere with content */
.fixed-cta-active {
  padding-bottom: 200px;
}
</style>
